/**
 * Typed GraphQL reads for the rules presets page (place manager, unit 11).
 * Read-only: nothing in this module writes.
 *
 * A rules preset is a `GatheringSettings` row with `preset: true`;
 * `admin_allGatheringSettingsPresets` lists those not archived (admin-only,
 * so through the staff door). Its rules live in `roles`: one list of role words per
 * action, as the backend stores them.
 *
 * No backend read goes from a preset to the rooms using it, so "where used" is
 * put together in the portal (`presets.ts`): every place with just its rooms'
 * presets ({@link PlacesWithRoomsQuery}), and the templates and repeats
 * (`place-schedule-api.ts`).
 *
 * `presets-api.test.ts` keeps each read under the backend's query cost limit.
 */

import { graphql, gqlRequestAs, type RequestOptions, type ResultOf } from '@/lib/graphql';
import type { RequestViewer } from '@/lib/request-headers';

/** One rules preset: who it is, its state and idle cleanups, and its roles. */
export const PresetFields = graphql(`
  fragment PresetFields on GatheringSettings {
    id
    name
    slug
    description
    iconUrl
    gatheringVisibility
    selectable
    sortScore
    state {
      official
      public
    }
    idleGatheringCleanupEnabled
    idleGatheringGracePeriodSeconds
    idleUserCleanupEnabled
    idleUserGracePeriodSeconds
    roles {
      adjustGameSettings
      advanceEventToFinalResults
      changeGame
      createSubgathering
      doGameAdmin
      endEvent
      endGame
      endGathering
      inviteToCall
      inviteToSeat
      inviteToStage
      kickMember
      modifyGatheringSettings
      muteUserGlobally
      removeFromSeatAndCall
      removeFromStage
      selfJoinCall
      selfJoinSeat
      selfJoinStage
      startGame
    }
  }
`);

/** A rules preset as the page reads it. */
export type PresetData = ResultOf<typeof PresetFields>;

/** What scheduling an event picks a preset by: its name and slug (an event
 *  names its presets by slug, so one with no slug cannot be picked). */
export type PresetChoice = Pick<PresetData, 'id' | 'name' | 'slug'>;

/** A preset's roles: one list of role words per action. */
export type PresetRoles = PresetData['roles'];

export const PresetsQuery = graphql(
  `
  query RulesPresets {
    admin_allGatheringSettingsPresets {
      ...PresetFields
    }
  }
`,
  [PresetFields],
);

/** Every rules preset not archived (STAFF ONLY: through the staff door). The
 *  one presets read: the presets page and the place page's schedule form both
 *  use it. Archived presets are NOT returned (`findPresetsOnly`: preset and
 *  not archived), though a room, a template or an event may still name one;
 *  the form then shows that preset by its slug and does not offer it. */
export async function fetchPresets(viewer: RequestViewer, options?: RequestOptions): Promise<PresetData[]> {
  const data = await gqlRequestAs(viewer, PresetsQuery, undefined, options);
  return data.admin_allGatheringSettingsPresets;
}

/** What "where used" needs of a place: its name, each room's title and rules
 *  preset (the main room and the open tables, 50 at most, with how many there
 *  are), and the preset new tables start with (null: the platform default;
 *  the main room has no such field). Not the place page's `PlaceFields`: that
 *  also reads each room's game, headcounts and first six people, for every
 *  table of every place, none of which this page shows. */
export const PlaceRoomsFields = graphql(`
  fragment PlaceRoomsFields on Place {
    id
    name
    rootGathering {
      id
      title
      settings {
        id
      }
    }
    subGatherings {
      totalCount
      nodes {
        id
        title
        settings {
          id
        }
      }
    }
    defaultGatheringSettingsForSubGatherings {
      id
    }
  }
`);

/** Every place in one read: `all: true` lifts the page size to the backend's
 *  cap (9999), so no place is read twice or skipped while places are made or
 *  removed between pages. It is not a `first:`, so the cost rule does not
 *  multiply it. */
export const PlacesWithRoomsQuery = graphql(
  `
  query PlacesWithRooms {
    admin_allPlaces(all: true) {
      ...PlaceRoomsFields
    }
  }
`,
  [PlaceRoomsFields],
);

/** A place with its rooms (the main room and the open tables, each with its
 *  rules preset) and its default for new tables. */
export type PlaceWithRoomsData = ResultOf<typeof PlacesWithRoomsQuery>['admin_allPlaces'][number];

/** Every place with its rooms (STAFF ONLY), newest first. */
export async function fetchPlacesWithRooms(viewer: RequestViewer, options?: RequestOptions): Promise<PlaceWithRoomsData[]> {
  const data = await gqlRequestAs(viewer, PlacesWithRoomsQuery, undefined, options);
  return data.admin_allPlaces;
}
